import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Users,
  CreditCard,
  MoreVertical,
  Bell,
  ArrowUpCircle,
  RefreshCw,
  Ban,
  CheckCircle2,
  ExternalLink,
  MessageSquare,
  StickyNote,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useSuperAdmin } from "@/hooks/use-super-admin";
import { addDays, format } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SuperAdminSubscriptionDialog } from "@/components/SuperAdminSubscriptionDialog";
import { SuperAdminExtendGraceDialog } from "@/components/SuperAdminExtendGraceDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { getBusinessPayoutDestination } from "@/lib/platform-settlements";
import { getBaseUrl } from "@/lib/site";
import {
  normalizeSalesFunnelStatus,
  salesCrmEventTitle,
  salesFunnelLabel,
  SALES_FUNNEL_STATUSES,
  type SalesCrmEvent,
  type SalesFunnelStatus,
} from "@/lib/sales-crm";

export default function SuperAdminBusinessDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isSuperAdmin } = useSuperAdmin();
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState<any>(null);
  const [owner, setOwner] = useState<any>(null);
  const [plan, setPlan] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"upgrade" | "renew" | "suspend" | "activate">("upgrade");
  const [extendGraceOpen, setExtendGraceOpen] = useState(false);
  const [pushTestLoading, setPushTestLoading] = useState<"all" | "owner" | null>(null);
  const [funnelStatus, setFunnelStatus] = useState<SalesFunnelStatus>("new");
  const [noteDraft, setNoteDraft] = useState("");
  const [crmEvents, setCrmEvents] = useState<SalesCrmEvent[]>([]);
  const [crmSaving, setCrmSaving] = useState(false);
  const [smsPhone, setSmsPhone] = useState("");
  const [smsMessage, setSmsMessage] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const { toast } = useToast();

  const loadCrmHistory = async (businessId: string) => {
    const { data, error } = await supabase
      .from("business_sales_crm_events" as any)
      .select("*")
      .eq("business_id", businessId)
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) {
      console.error("Error loading CRM history:", error);
      return;
    }
    setCrmEvents((data as SalesCrmEvent[]) || []);
  };

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate("/app");
      return;
    }
    if (id) {
      fetchBusinessDetails();
    }
  }, [id, isSuperAdmin, navigate]);

  const fetchBusinessDetails = async () => {
    try {
      setLoading(true);

      // Fetch business
      const { data: businessData, error: businessError } = await supabase
        .from("businesses")
        .select("*")
        .eq("id", id)
        .single();

      if (businessError) throw businessError;
      setBusiness(businessData);
      setFunnelStatus(normalizeSalesFunnelStatus(businessData.sales_funnel_status));
      setNoteDraft("");

      // Fetch owner
      if (businessData?.owner_user_id) {
        const { data: ownerData } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", businessData.owner_user_id)
          .single();
        setOwner(ownerData);
        setSmsPhone(String(businessData.phone || ownerData?.phone || ""));
      } else {
        setSmsPhone(String(businessData.phone || ""));
      }

      // Fetch plan
      if (businessData?.plan_id) {
        const { data: planData } = await supabase
          .from("subscription_plans")
          .select("*")
          .eq("id", businessData.plan_id)
          .single();
        setPlan(planData);
      }

      // Fetch stats + CRM history
      const [appointmentsCount, customersCount, locationsCount, staffCount] = await Promise.all([
        supabase.from("appointments").select("*", { count: "exact", head: true }).eq("business_id", id),
        supabase.from("customers").select("*", { count: "exact", head: true }).eq("business_id", id),
        supabase.from("locations").select("*", { count: "exact", head: true }).eq("business_id", id),
        supabase.from("staff").select("*", { count: "exact", head: true }).eq("business_id", id),
      ]);
      await loadCrmHistory(String(id));

      setStats({
        appointments: appointmentsCount.count || 0,
        customers: customersCount.count || 0,
        locations: locationsCount.count || 0,
        staff: staffCount.count || 0,
      });
    } catch (error) {
      console.error("Error fetching business details:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveCrm = async () => {
    if (!business?.id) return;
    const note = noteDraft.trim();
    const previousStatus = normalizeSalesFunnelStatus(business.sales_funnel_status);
    const statusChanged = funnelStatus !== previousStatus;
    if (!statusChanged && !note) {
      toast({
        title: "Nothing to save",
        description: "Change the funnel stage or add a note.",
      });
      return;
    }

    try {
      setCrmSaving(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const updatedAt = new Date().toISOString();
      const eventRows: Array<Record<string, unknown>> = [];

      if (statusChanged) {
        eventRows.push({
          business_id: business.id,
          event_type: "status_change",
          from_status: previousStatus,
          to_status: funnelStatus,
          created_by: user?.id ?? null,
        });
      }
      if (note) {
        eventRows.push({
          business_id: business.id,
          event_type: "note",
          body: note,
          created_by: user?.id ?? null,
        });
      }

      const { error: eventsError } = await supabase
        .from("business_sales_crm_events" as any)
        .insert(eventRows as any);
      if (eventsError) throw eventsError;

      const { error } = await supabase
        .from("businesses")
        .update({
          sales_funnel_status: funnelStatus,
          sales_notes: note || business.sales_notes || null,
          sales_funnel_updated_at: updatedAt,
        } as any)
        .eq("id", business.id);
      if (error) throw error;

      setBusiness((prev: any) =>
        prev
          ? {
              ...prev,
              sales_funnel_status: funnelStatus,
              sales_notes: note || prev.sales_notes || null,
              sales_funnel_updated_at: updatedAt,
            }
          : prev,
      );
      setNoteDraft("");
      await loadCrmHistory(business.id);
      toast({
        title: "CRM saved",
        description: statusChanged
          ? `Funnel set to ${salesFunnelLabel(funnelStatus)}.`
          : "Note added to history.",
      });
    } catch (e) {
      console.error(e);
      toast({
        title: "Could not save CRM",
        description: e instanceof Error ? e.message : "Update failed.",
        variant: "destructive",
      });
    } finally {
      setCrmSaving(false);
    }
  };

  const sendCustomSms = async () => {
    if (!business?.id) return;
    const phone = smsPhone.trim();
    const message = smsMessage.trim();
    if (!phone || !message) {
      toast({
        title: "Phone and message required",
        description: "Enter a destination phone number and SMS body.",
        variant: "destructive",
      });
      return;
    }
    try {
      setSmsSending(true);
      const { data, error } = await supabase.functions.invoke("send-sms", {
        body: {
          businessId: business.id,
          phone,
          message,
          context: "superadmin_custom_sms",
          contextId: business.id,
        },
      });
      if (error) throw error;
      if (data && (data as any).ok === false) {
        throw new Error((data as any).error || "SMS send failed");
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      const previousStatus = normalizeSalesFunnelStatus(business.sales_funnel_status);
      const nextStatus: SalesFunnelStatus = previousStatus === "new" ? "contacted" : previousStatus;
      const updatedAt = new Date().toISOString();
      const eventRows: Array<Record<string, unknown>> = [
        {
          business_id: business.id,
          event_type: "sms",
          body: message,
          phone,
          created_by: user?.id ?? null,
        },
      ];
      if (nextStatus !== previousStatus) {
        eventRows.push({
          business_id: business.id,
          event_type: "status_change",
          from_status: previousStatus,
          to_status: nextStatus,
          created_by: user?.id ?? null,
        });
      }

      await supabase.from("business_sales_crm_events" as any).insert(eventRows as any);
      await supabase
        .from("businesses")
        .update({
          sales_funnel_status: nextStatus,
          sales_funnel_updated_at: updatedAt,
          sales_notes: message,
        } as any)
        .eq("id", business.id);

      setFunnelStatus(nextStatus);
      setBusiness((prev: any) =>
        prev
          ? {
              ...prev,
              sales_funnel_status: nextStatus,
              sales_funnel_updated_at: updatedAt,
              sales_notes: message,
            }
          : prev,
      );
      setSmsMessage("");
      await loadCrmHistory(business.id);
      toast({
        title: "SMS sent",
        description: `Message delivered to ${phone}.`,
      });
    } catch (e) {
      console.error(e);
      toast({
        title: "SMS failed",
        description: e instanceof Error ? e.message : "Could not send SMS.",
        variant: "destructive",
      });
    } finally {
      setSmsSending(false);
    }
  };

  const runPushTestSuite = async (scope: "all" | "owner") => {
    if (!business?.id) return;
    try {
      setPushTestLoading(scope);
      const ownerId = business?.owner_user_id ? String(business.owner_user_id) : "";
      const { data, error } = await supabase.functions.invoke("superadmin-push-test-suite", {
        body: {
          business_id: business.id,
          ...(scope === "owner" && ownerId ? { user_id: ownerId } : {}),
        },
      });
      if (error) throw error;
      const total = typeof (data as any)?.total_sent_across_scenarios === "number"
        ? (data as any).total_sent_across_scenarios
        : 0;
      toast({
        title: "Push test suite finished",
        description:
          scope === "owner"
            ? `Owner-scoped: ${total} OneSignal send(s) recorded across ${(data as any)?.scenarios_run ?? "?"} scenarios.`
            : `All devices for business: ${total} OneSignal send(s) across ${(data as any)?.scenarios_run ?? "?"} scenarios.`,
      });
    } catch (e) {
      console.error(e);
      toast({
        title: "Push test suite failed",
        description: e instanceof Error ? e.message : "Could not run push test suite.",
        variant: "destructive",
      });
    } finally {
      setPushTestLoading(null);
    }
  };

  if (!isSuperAdmin) {
    return null;
  }

  if (loading) {
    return (
      <AppShell businessName="Super Admin">
        <PageHeader title="Business Details" />
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppShell>
    );
  }

  if (!business) {
    return (
      <AppShell businessName="Super Admin">
        <PageHeader title="Business Details" />
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Business not found</p>
            <Button onClick={() => navigate("/app/super-admin")} className="mt-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </AppShell>
    );
  }

  const trialEndsAt = business?.trial_ends_at ? new Date(business.trial_ends_at) : null;
  const bookingPageUrl = business?.slug ? `${getBaseUrl()}/${business.slug}` : "";
  const payoutDestination = getBusinessPayoutDestination(business);
  const trialGraceEndsAt = trialEndsAt ? addDays(trialEndsAt, 7) : null;
  const subscriptionGraceEndsAt = business?.subscription_grace_period_ends_at
    ? new Date(business.subscription_grace_period_ends_at)
    : null;
  const isInTrialGraceWindow =
    business?.subscription_status === "trial" &&
    !!trialEndsAt &&
    trialEndsAt.getTime() <= Date.now() &&
    !!trialGraceEndsAt &&
    Date.now() < trialGraceEndsAt.getTime();

  return (
    <AppShell businessName="Super Admin">
      <PageHeader
        title={business.name}
        description={`Business ID: ${business.id}`}
        action={
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={!!pushTestLoading}
              onClick={() => void runPushTestSuite("all")}
            >
              {pushTestLoading === "all" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Bell className="mr-2 h-4 w-4" />
              )}
              Send push test suite
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <MoreVertical className="mr-2 h-4 w-4" />
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => {
                  setDialogMode("upgrade");
                  setDialogOpen(true);
                }}>
                  <ArrowUpCircle className="mr-2 h-4 w-4" />
                  Change Plan
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  setDialogMode("renew");
                  setDialogOpen(true);
                }}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Renew Subscription
                </DropdownMenuItem>
                {business.subscription_status === "active" ? (
                  <DropdownMenuItem onClick={() => {
                    setDialogMode("suspend");
                    setDialogOpen(true);
                  }}>
                    <Ban className="mr-2 h-4 w-4" />
                    Suspend Business
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => {
                    setDialogMode("activate");
                    setDialogOpen(true);
                  }}>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Activate Business
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setExtendGraceOpen(true)}>
                  <Calendar className="mr-2 h-4 w-4" />
                  Extend Grace Period
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={!!pushTestLoading || !business?.owner_user_id}
                  onClick={() => void runPushTestSuite("owner")}
                >
                  {pushTestLoading === "owner" ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Bell className="mr-2 h-4 w-4" />
                  )}
                  Send push tests (owner only)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" onClick={() => navigate("/app/super-admin")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </div>
        }
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Appointments</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.appointments.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Customers</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.customers.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Locations</CardTitle>
              <MapPin className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.locations.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Staff</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.staff.toLocaleString()}</div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="subscription">Subscription</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Business Information */}
            <Card>
              <CardHeader>
                <CardTitle>Business Information</CardTitle>
                <CardDescription>Basic business details and contact information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Name</p>
                  <p className="text-base font-semibold">{business.name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Booking page</p>
                  {bookingPageUrl ? (
                    <a
                      href={bookingPageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-base font-medium text-primary hover:underline break-all"
                    >
                      {bookingPageUrl}
                      <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                    </a>
                  ) : (
                    <p className="text-base text-muted-foreground">No booking URL</p>
                  )}
                </div>
                {business.phone && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Phone
                    </p>
                    <p className="text-base">{business.phone}</p>
                  </div>
                )}
                {business.email && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email
                    </p>
                    <p className="text-base">{business.email}</p>
                  </div>
                )}
                {business.address && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Address
                    </p>
                    <p className="text-base">{business.address}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Primary Module</p>
                  <Badge variant="outline" className="capitalize">
                    {business.primary_module || "appointments"}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Created
                  </p>
                  <p className="text-base">{format(new Date(business.created_at), "PPP")}</p>
                </div>
              </CardContent>
            </Card>

            {/* Owner Information */}
            <Card>
              <CardHeader>
                <CardTitle>Owner Information</CardTitle>
                <CardDescription>Business owner account details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {owner ? (
                  <>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Name</p>
                      <p className="text-base font-semibold">{owner.name || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Email
                      </p>
                      <p className="text-base">{owner.email || "N/A"}</p>
                    </div>
                    {owner.phone ? (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          Phone
                        </p>
                        <p className="text-base">{owner.phone}</p>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Owner information not available</p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <StickyNote className="h-5 w-5" />
                  Sales CRM
                </CardTitle>
                <CardDescription>
                  Track where you are in the sales funnel and keep internal notes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="sales-funnel">Funnel stage</Label>
                  <Select
                    value={funnelStatus}
                    onValueChange={(v) => setFunnelStatus(normalizeSalesFunnelStatus(v))}
                  >
                    <SelectTrigger id="sales-funnel">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SALES_FUNNEL_STATUSES.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {business.sales_funnel_updated_at ? (
                    <p className="text-xs text-muted-foreground">
                      Last updated {format(new Date(business.sales_funnel_updated_at), "PPp")}
                    </p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sales-notes">Add note</Label>
                  <Textarea
                    id="sales-notes"
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    placeholder="Call notes, objections, next steps…"
                    rows={3}
                  />
                </div>
                <Button type="button" onClick={() => void saveCrm()} disabled={crmSaving}>
                  {crmSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Save to history
                </Button>

                <div className="space-y-2 border-t pt-4">
                  <p className="text-sm font-medium">History</p>
                  {crmEvents.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No CRM activity yet.</p>
                  ) : (
                    <div className="max-h-80 space-y-3 overflow-y-auto pr-1">
                      {crmEvents.map((event) => (
                        <div key={event.id} className="rounded-md border bg-muted/30 p-3">
                          <div className="mb-1 flex flex-wrap items-center gap-2">
                            <Badge variant="outline" className="capitalize">
                              {event.event_type === "status_change"
                                ? "Stage"
                                : event.event_type === "sms"
                                  ? "SMS"
                                  : "Note"}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(event.created_at), "PPp")}
                            </span>
                          </div>
                          <p className="text-sm font-medium">{salesCrmEventTitle(event)}</p>
                          {event.body ? (
                            <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                              {event.body}
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Send SMS
                </CardTitle>
                <CardDescription>
                  Send a custom SMS to the business owner or contact number
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="sms-phone">Phone</Label>
                  <Input
                    id="sms-phone"
                    value={smsPhone}
                    onChange={(e) => setSmsPhone(e.target.value)}
                    placeholder="+233…"
                    inputMode="tel"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sms-message">Message</Label>
                  <Textarea
                    id="sms-message"
                    value={smsMessage}
                    onChange={(e) => setSmsMessage(e.target.value)}
                    placeholder="Write your message…"
                    rows={6}
                  />
                  <p className="text-xs text-muted-foreground">{smsMessage.length} characters</p>
                </div>
                <Button
                  type="button"
                  onClick={() => void sendCustomSms()}
                  disabled={smsSending || !smsPhone.trim() || !smsMessage.trim()}
                >
                  {smsSending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <MessageSquare className="mr-2 h-4 w-4" />
                  )}
                  Send SMS
                </Button>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Payout Details
              </CardTitle>
              <CardDescription>
                MoMo or bank account for disbursing customer payments collected via Buukd
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">{payoutDestination.methodLabel}</Badge>
                {payoutDestination.connected && payoutDestination.accountDetail ? (
                  <Badge className="bg-success/10 text-success border-success/20">Connected</Badge>
                ) : (
                  <Badge variant="outline">Missing details</Badge>
                )}
              </div>

              {payoutDestination.connected && payoutDestination.accountDetail ? (
                <div className="rounded-lg border bg-muted/40 p-4 space-y-2">
                  {payoutDestination.providerLabel ? (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        {payoutDestination.method === "bank" ? "Bank" : "Network"}
                      </p>
                      <p className="text-base font-semibold">{payoutDestination.providerLabel}</p>
                    </div>
                  ) : null}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      {payoutDestination.method === "bank" ? "Account number" : "Mobile number"}
                    </p>
                    <p className="font-mono text-lg font-semibold">{payoutDestination.accountDetail}</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  This business has not connected a MoMo or bank payout account in Settings → Payments & Payouts.
                </p>
              )}

              <Button variant="outline" size="sm" asChild>
                <Link to="/app/platform/payouts">Open Settlements</Link>
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subscription" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Subscription Details</CardTitle>
              <CardDescription>Current subscription plan and status</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Plan</p>
                <Badge variant="outline" className="text-base">
                  {plan?.name || business.plan_id || "Unknown"}
                </Badge>
                {plan?.price_ghs && (
                  <p className="text-sm text-muted-foreground mt-1">₵{plan.price_ghs}/month</p>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Status</p>
                <Badge variant={business.subscription_status === "active" ? "default" : "secondary"}>
                  {business.subscription_status}
                </Badge>
              </div>
              {plan && (
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium text-muted-foreground mb-2">Plan Limits</p>
                  <div className="grid gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Monthly Bookings:</span>
                      <span className="font-medium">{plan.booking_limit_monthly === null ? "Unlimited" : plan.booking_limit_monthly.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Staff:</span>
                      <span className="font-medium">{plan.staff_limit === null ? "Unlimited" : plan.staff_limit.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Locations:</span>
                      <span className="font-medium">{plan.location_limit === null ? "Unlimited" : plan.location_limit.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Customers:</span>
                      <span className="font-medium">{plan.customer_limit_cumulative === null ? "Unlimited" : plan.customer_limit_cumulative.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Grace Period</CardTitle>
              <CardDescription>
                Trial grace is locked at 7 days after trial ends. Subscription grace uses{" "}
                <span className="font-medium">subscription_grace_period_ends_at</span>.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 md:grid-cols-3">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Trial ends</p>
                  <p className="text-sm">
                    {trialEndsAt ? format(trialEndsAt, "PPP p") : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Trial grace ends</p>
                  <p className="text-sm">
                    {trialGraceEndsAt ? format(trialGraceEndsAt, "PPP p") : "—"}
                  </p>
                  {isInTrialGraceWindow ? (
                    <p className="text-xs text-muted-foreground mt-1">
                      Currently in the trial grace window (trial ended).
                    </p>
                  ) : null}
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Subscription grace ends</p>
                  <p className="text-sm">
                    {subscriptionGraceEndsAt ? format(subscriptionGraceEndsAt, "PPP p") : "—"}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                <Button variant="outline" onClick={() => setExtendGraceOpen(true)}>
                  Extend grace…
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <SuperAdminSubscriptionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        business={business}
        mode={dialogMode}
        onSuccess={() => {
          fetchBusinessDetails();
        }}
      />

      <SuperAdminExtendGraceDialog
        open={extendGraceOpen}
        onOpenChange={setExtendGraceOpen}
        business={business ? { id: business.id, name: business.name, subscription_status: business.subscription_status } : null}
        defaultDays={7}
        onSuccess={() => {
          fetchBusinessDetails();
        }}
      />
    </AppShell>
  );
}
